import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ClipboardList, Clock, CheckCircle, AlertCircle, User,
  Send, Archive, Edit, Eye, X, RefreshCw, History, Users, FileText,
  MessageSquare, PenTool, Calendar, Phone
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { WhatsAppButton } from '../components/WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'قيد الانتظار', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'in_progress', label: 'قيد المعالجة', color: 'bg-blue-100 text-blue-700' },
  { value: 'under_review', label: 'تحت المراجعة', color: 'bg-purple-100 text-purple-700' },
  { value: 'transferred', label: 'محال', color: 'bg-orange-100 text-orange-700' },
  { value: 'completed', label: 'مكتمل', color: 'bg-green-100 text-green-700' },
  { value: 'archived', label: 'مؤرشف', color: 'bg-gray-100 text-gray-700' },
];

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

export default function MyAssignedTasks() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [clientRequests, setClientRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [itemActions, setItemActions] = useState([]);
  
  // حالات الإجراء
  const [actionType, setActionType] = useState('update');
  const [actionText, setActionText] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [savingAction, setSavingAction] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
    }
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // جلب المهام المحالة للموظف
      const tasksRes = await axios.get(`${API}/tasks/my-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      // جلب الطلبات المحالة للموظف
      const requestsRes = await axios.get(`${API}/my-assigned-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      // جلب قائمة الموظفين
      const employeesRes = await axios.get(`${API}/employees?include_admins=true`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      
      setTasks(tasksRes.data || []);
      setClientRequests(requestsRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const fetchItemActions = async (itemId, itemType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/requests/${itemType}/${itemId}/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItemActions(response.data || []);
    } catch (err) {
      console.error('Error fetching actions:', err);
      setItemActions([]);
    }
  };

  const openActionModal = async (item, itemType) => {
    setSelectedItem({ ...item, itemType });
    await fetchItemActions(item.id, itemType);
    setShowActionModal(true);
    setActionType('update');
    setActionText('');
    setSelectedEmployees([]);
    setNewStatus('');
  };

  const submitAction = async () => {
    if (!actionText.trim()) {
      alert('يرجى كتابة تفاصيل الإجراء');
      return;
    }
    
    setSavingAction(true);
    try {
      const token = localStorage.getItem('token');
      const actionData = {
        action_type: actionType,
        action_text: actionText,
        new_status: newStatus || undefined,
        new_assigned_to: selectedEmployees.length > 0 ? selectedEmployees.map(e => e.id) : undefined,
        new_assigned_to_names: selectedEmployees.length > 0 ? selectedEmployees.map(e => e.full_name) : undefined
      };
      
      await axios.post(
        `${API}/requests/${selectedItem.itemType}/${selectedItem.id}/actions`,
        actionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // إشعار العميل
      const phone = selectedItem.client_phone || selectedItem.phone_number;
      if (phone && actionType !== 'update') {
        const notify = window.confirm('هل تريد إشعار العميل بهذا الإجراء عبر واتساب؟');
        if (notify) {
          sendWhatsAppNotification(phone, selectedItem.client_name, actionText);
        }
      }
      
      alert('تم إضافة الإجراء بنجاح');
      setShowActionModal(false);
      fetchData();
    } catch (err) {
      console.error('Error submitting action:', err);
      alert('حدث خطأ');
    }
    setSavingAction(false);
  };

  const sendWhatsAppNotification = (phone, clientName, actionText) => {
    let cleanNumber = phone.replace(/[\s\-\(\)]/g, '');
    if (cleanNumber.startsWith('05')) cleanNumber = '966' + cleanNumber.substring(1);
    else if (cleanNumber.startsWith('5')) cleanNumber = '966' + cleanNumber;
    else if (!cleanNumber.startsWith('966') && !cleanNumber.startsWith('+')) cleanNumber = '966' + cleanNumber;
    cleanNumber = cleanNumber.replace('+', '');
    
    const message = `مرحباً ${clientName}،\n\nمكتب المحامي هشام يوسف الخياط يُحييك.\n\n📋 تحديث على طلبك:\n${actionText}\n\nللاستفسار، نحن في خدمتك.`;
    window.open(`https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return <span className={`px-2 py-1 rounded-full text-xs ${opt?.color || 'bg-gray-100 text-gray-600'}`}>{opt?.label || status}</span>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'case': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'consultation': return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'notary': return <PenTool className="w-4 h-4 text-purple-500" />;
      default: return <ClipboardList className="w-4 h-4 text-gold" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA');
  };

  // إحصائيات سريعة
  const stats = {
    total: tasks.length + clientRequests.length,
    pending: [...tasks, ...clientRequests].filter(i => i.status === 'pending').length,
    inProgress: [...tasks, ...clientRequests].filter(i => i.status === 'in_progress').length,
    completed: [...tasks, ...clientRequests].filter(i => i.status === 'completed').length,
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')}><ArrowRight className="w-6 h-6 text-white" /></button>
            <Logo size="md" />
            <h1 className="text-xl font-heading font-bold text-white">مهامي المحالة</h1>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* بطاقات الإحصائيات */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border-r-4 border-primary">
            <p className="text-2xl font-bold text-primary">{stats.total}</p>
            <p className="text-sm text-gray-500">إجمالي المهام</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-r-4 border-yellow-500">
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-gray-500">قيد الانتظار</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-r-4 border-blue-500">
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
            <p className="text-sm text-gray-500">قيد المعالجة</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-r-4 border-green-500">
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            <p className="text-sm text-gray-500">مكتملة</p>
          </div>
        </div>

        {/* التبويبات */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setActiveTab('tasks')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === 'tasks' ? 'bg-gold text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            <ClipboardList className="w-4 h-4" />
            المهام ({tasks.length})
          </button>
          <button onClick={() => setActiveTab('requests')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === 'requests' ? 'bg-gold text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            <FileText className="w-4 h-4" />
            الطلبات المحالة ({clientRequests.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* عرض المهام */}
            {activeTab === 'tasks' && tasks.map((task, idx) => (
              <div key={idx} className={`bg-white rounded-xl p-5 shadow-sm border-r-4 ${PRIORITY_COLORS[task.priority] || 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-sm text-gold">{task.task_number}</span>
                      {getStatusBadge(task.status)}
                      <span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_COLORS[task.priority]}`}>
                        {task.priority === 'high' ? 'عاجل' : task.priority === 'medium' ? 'متوسط' : 'عادي'}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-primary mb-1">{task.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {task.client_name || '-'}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {task.client_phone || '-'}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(task.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => openActionModal(task, 'task')}
                      className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark text-sm"
                    >
                      <Send className="w-4 h-4" />
                      إجراء
                    </button>
                    {task.client_phone && (
                      <WhatsAppButton phoneNumber={task.client_phone} clientName={task.client_name} size="sm" />
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* عرض الطلبات المحالة */}
            {activeTab === 'requests' && clientRequests.map((req, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border-r-4 border-blue-300">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getTypeIcon(req.request_type)}
                      <span className="font-mono text-sm text-gold">{req.request_number || req.consultation_number}</span>
                      {getStatusBadge(req.status)}
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        {req.request_type === 'case' ? 'قضية' : req.request_type === 'consultation' ? 'استشارة' : 'خدمة موثق'}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-primary mb-1">{req.subject || req.service_type}</h3>
                    <p className="text-sm text-gray-600 mb-2">{req.description || req.details}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {req.client_name}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {req.phone_number || '-'}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(req.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => openActionModal(req, req.request_type || 'case')}
                      className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark text-sm"
                    >
                      <Send className="w-4 h-4" />
                      إجراء
                    </button>
                    {req.phone_number && (
                      <WhatsAppButton phoneNumber={req.phone_number} clientName={req.client_name} size="sm" />
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* رسالة فارغة */}
            {((activeTab === 'tasks' && tasks.length === 0) || (activeTab === 'requests' && clientRequests.length === 0)) && (
              <div className="bg-white rounded-xl p-12 text-center">
                <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-body">لا توجد مهام محالة إليك حالياً</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* مودال الإجراء */}
      {showActionModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-l from-primary to-primary-800 text-white rounded-t-xl">
              <h3 className="text-lg font-heading font-semibold">تسجيل إجراء / إحالة</h3>
              <button onClick={() => setShowActionModal(false)} className="p-2 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* معلومات المهمة */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">الرقم:</span> <strong>{selectedItem.task_number || selectedItem.request_number}</strong></div>
                  <div><span className="text-gray-500">العميل:</span> <strong>{selectedItem.client_name}</strong></div>
                  <div><span className="text-gray-500">الحالة:</span> {getStatusBadge(selectedItem.status)}</div>
                  <div><span className="text-gray-500">النوع:</span> {selectedItem.itemType}</div>
                </div>
              </div>

              {/* سجل الإجراءات السابقة */}
              {itemActions.length > 0 && (
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-3 bg-gray-50 border-b flex items-center gap-2">
                    <History className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-sm">سجل الإجراءات ({itemActions.length})</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto divide-y">
                    {itemActions.map((action, idx) => (
                      <div key={idx} className="p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-primary">{action.performed_by_name}</span>
                          <span className="text-xs text-gray-400">{new Date(action.created_at).toLocaleString('ar-SA')}</span>
                        </div>
                        <p className="text-gray-700">{action.action_text}</p>
                        {action.new_assigned_to_names && (
                          <p className="text-xs text-blue-600 mt-1">➜ تم الإحالة إلى: {action.new_assigned_to_names.join(', ')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* نموذج الإجراء */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">نوع الإجراء</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'update', label: 'تحديث حالة', icon: Edit },
                      { value: 'assign', label: 'إحالة لموظف', icon: Send },
                      { value: 'transfer', label: 'نقل للإدارة', icon: Users },
                      { value: 'archive', label: 'حفظ بالأرشيف', icon: Archive }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setActionType(opt.value)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          actionType === opt.value ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary'
                        }`}
                      >
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">تفاصيل الإجراء *</label>
                  <textarea
                    value={actionText}
                    onChange={(e) => setActionText(e.target.value)}
                    placeholder="اكتب ما تم إنجازه أو التعليمات للموظف التالي..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold"
                  />
                </div>

                {(actionType === 'assign' || actionType === 'transfer') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">إحالة إلى</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {employees.filter(e => e.id !== user?.id).map(emp => (
                        <button
                          key={emp.id}
                          onClick={() => {
                            if (selectedEmployees.find(e => e.id === emp.id)) {
                              setSelectedEmployees(selectedEmployees.filter(e => e.id !== emp.id));
                            } else {
                              setSelectedEmployees([...selectedEmployees, emp]);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            selectedEmployees.find(e => e.id === emp.id)
                              ? 'bg-gold text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {emp.full_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">تغيير الحالة</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="">بدون تغيير</option>
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={submitAction}
                  disabled={savingAction || !actionText.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50"
                >
                  {savingAction ? <RefreshCw className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  حفظ الإجراء
                </button>
                <button onClick={() => setShowActionModal(false)} className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
